import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { IPlatformHeaderContent } from '@/shared/types/platform-pages';
import { useCreateAccountProvider } from '@/lib/context';
import { useRouter } from 'next/router';
import { useWindowSize } from 'react-use';
import ModuleStarterCardsSection from '../ModuleStarterCardsSection';
import { isEmpty, isNil, map } from 'lodash-es';
import { buildCloudFrontURL } from '@pietra/utils';
import './index.less';

interface IProps {
  content: IPlatformHeaderContent;
  customTitle?: React.ReactNode;
  customActionBtn?: React.ReactNode;
  customHero?: React.ReactNode;
}

const PlatformHeader = ({ content, customTitle, customActionBtn, customHero }: IProps) => {
  const { showSignupDialog } = useCreateAccountProvider();
  const {
    platformName,
    heroImage,
    title,
    description,
    backgroundColor,
    color,
    companyLogos,
    compareToLogos,
    comparison,
    btnText,
    bookDemoPath,
    popularUseCases,
    titleClass,
    descriptionClass,
  } = content;

  const router = useRouter();
  const { width } = useWindowSize();
  const [isWide, setIsWide] = useState<boolean>();

  useEffect(() => {
    setIsWide(() => width >= 640);
  }, [width]);

  return (
    <div
      className={classNames('platform-header-container', {
        [platformName.replaceAll(' ', '-').toLocaleLowerCase()]: true,
      })}
      id="platform-header"
      style={{ backgroundColor }}
    >
      <div className="platform-header">
        <div className="header">
          {!isEmpty(customTitle) ? (
            customTitle
          ) : (
            <h1 className={classNames('title', titleClass)} style={color ? { color } : null}>
              {title}
            </h1>
          )}

          {!isEmpty(description) && (
            <p className={classNames('description', descriptionClass)} style={color ? { color, opacity: 0.8 } : null}>
              {description}
            </p>
          )}

          {!isEmpty(customActionBtn) && customActionBtn}

          {!isEmpty(btnText) && (
            <div className="hero-btn-container">
              <div
                className="hero-btn secondary"
                onClick={() => {
                  showSignupDialog();
                }}
              >
                <span>{btnText ?? 'Get started for free'}</span>
              </div>
              <div
                className="hero-btn"
                onClick={() => {
                  router.push({ pathname: bookDemoPath });
                }}
              >
                <span>Book a demo</span>
              </div>
            </div>
          )}
        </div>

        {!isEmpty(heroImage) && (
          <div className="image-and-access-form">
            <div className="hero-image">
              <Image
                width={heroImage.width}
                height={heroImage.height}
                src={heroImage.url}
                alt={platformName}
                sizes="(min-width: 1320px) 1100px, calc(100vw - 32px)"
              />
            </div>
          </div>
        )}

        {!isEmpty(customHero) && customHero}

        {!isEmpty(compareToLogos ?? []) && (
          <div className="compare-to-container">
            <p className="compare-to-title">Compare to</p>
            <div className="compare-to-logos">
              {map(compareToLogos, (img) => {
                return <Image key={img.url} width={img.width} height={img.height} src={img.url} alt={platformName} />;
              })}
            </div>
          </div>
        )}

        {!isNil(comparison) && (
          <div className="card-container">
            <div className="member-pricing-card">
              <div className="title">
                <Image width={61} height={16} src="/images/header/small-logo.png" alt="Pietra" title="Pietra" />
                <p style={comparison.memberPricingStyle}>Member pricing</p>
              </div>
              <div className="pricing-limits">
                <p className="pricing">{comparison.pricing}</p>
                <p className="limits">{comparison.limits}</p>
              </div>
            </div>
            <div className="competitor-cost-card">
              <p style={{ color }}>Competitor software cost</p>
              <div className="pricing-limits">
                <p className="pricing" style={comparison.competitorPricing?.style}>
                  {comparison.competitorPricing.content}
                </p>
                <p className="limits" style={comparison.competitorLimits?.style}>
                  {comparison.competitorLimits.content}
                </p>
              </div>
            </div>
          </div>
        )}

        {!isEmpty(companyLogos ?? []) && (
          <div className="companies">
            <div className="title" style={{ color, opacity: 0.7 }}>
              {companyLogos.title}
            </div>
            <div className="company-logos">
              {map(companyLogos.logos, ({ url, name, width, height }) => (
                <Image src={url} key={url} alt={name} width={width} height={height} />
              ))}
            </div>
          </div>
        )}

        {!isEmpty(popularUseCases) && (
          <ModuleStarterCardsSection title={popularUseCases.title} cards={popularUseCases.cards} />
        )}
      </div>
    </div>
  );
};

export default PlatformHeader;
